// ============================================================================
// Auth provider injection
// ============================================================================

let _authenticatedFetch: ((path: string, options?: RequestInit) => Promise<Response>) | null = null;

/**
 * Called by AIAssistantProvider on mount to inject authenticated fetch.
 */
export function setAssistantServiceAuthProvider(
  apiBaseUrl: string,
  getHeaders: () => Promise<HeadersInit>,
): void {
  _authenticatedFetch = async (path: string, options?: RequestInit): Promise<Response> => {
    const headers = await getHeaders();
    return fetch(`${apiBaseUrl}${path}`, {
      ...options,
      headers: {
        ...headers,
        ...options?.headers,
      },
    });
  };
}

// ============================================================================
// Types
// ============================================================================

export interface AIAssistantSessionDto {
  id: string;
  userId: string;
  title: string | null;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
}

export interface AIAssistantSessionDetailDto {
  id: string;
  userId: string;
  title: string | null;
  createdAt: string;
  updatedAt: string;
  messages: AIAssistantMessageDto[];
}

export interface AIAssistantMessageDto {
  id: string;
  role: string;
  content: string;
  timestamp: number;
  toolCall?: {
    id: string;
    name: string;
    status: string;
    result?: unknown;
  };
}

export interface CreateSessionRequest {
  title?: string;
}

export interface AddMessageRequest {
  role: string;
  content: string;
  timestamp: number;
  toolCall?: {
    id: string;
    name: string;
    status: string;
    result?: unknown;
  };
}

// ============================================================================
// Service
// ============================================================================

class AIAssistantService {
  private readonly baseUrl = '/api/v1/assistant';

  private get fetch(): (path: string, options?: RequestInit) => Promise<Response> {
    if (!_authenticatedFetch) {
      throw new Error('AI Assistant service auth provider not initialised.');
    }
    return _authenticatedFetch;
  }

  async getSessions(): Promise<AIAssistantSessionDto[]> {
    const response = await this.fetch(`${this.baseUrl}/sessions`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message ?? `Failed to get sessions: ${response.status}`);
    }

    return response.json();
  }

  async getSession(sessionId: string): Promise<AIAssistantSessionDetailDto> {
    const response = await this.fetch(`${this.baseUrl}/sessions/${sessionId}`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message ?? `Failed to get session: ${response.status}`);
    }

    return response.json();
  }

  async createSession(request?: CreateSessionRequest): Promise<AIAssistantSessionDto> {
    const response = await this.fetch(`${this.baseUrl}/sessions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request ?? {}),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message ?? `Failed to create session: ${response.status}`);
    }

    return response.json();
  }

  async addMessage(sessionId: string, request: AddMessageRequest): Promise<AIAssistantMessageDto> {
    const response = await this.fetch(`${this.baseUrl}/sessions/${sessionId}/messages`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message ?? `Failed to add message: ${response.status}`);
    }

    return response.json();
  }

  async addMessages(sessionId: string, requests: AddMessageRequest[]): Promise<AIAssistantMessageDto[]> {
    if (requests.length === 0) return [];
    if (requests.length === 1) {
      const single = await this.addMessage(sessionId, requests[0]);
      return [single];
    }
    const response = await this.fetch(`${this.baseUrl}/sessions/${sessionId}/messages/batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ messages: requests }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message ?? `Failed to add messages: ${response.status}`);
    }

    return response.json();
  }

  async deleteSession(sessionId: string): Promise<void> {
    const response = await this.fetch(`${this.baseUrl}/sessions/${sessionId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message ?? `Failed to delete session: ${response.status}`);
    }
  }
}

export const aiAssistantService = new AIAssistantService();
